import {
  type ProgramTaskCompleteInput,
  type ProgramTaskCompleteSuccess,
  type TaskData,
  type TaskId,
  toResponseError,
} from '@terros/common'
import { mutationUpdate, type UpdateHookResponse } from '../hooks'
import { useTantalimMutation } from '../mutation'
import { useTaskCache } from './cache'

export function useCompleteTask(): UpdateHookResponse<TaskData> {
  const mutation = useTantalimMutation<ProgramTaskCompleteInput, ProgramTaskCompleteSuccess>({
    url: '/program/task/complete',
  })
  const cache = useTaskCache()

  const complete = async (task: TaskData): Promise<TaskData> => {
    try {
      const { task: completedTask } = await mutation.mutateAsync({ taskId: task.taskId })
      cache.replaceItem(completedTask)
      return completedTask
    } catch (error) {
      cache.replaceItem(task)
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, complete)
}

/**
 * For callers that only know the task id (e.g. the web video page opened from the Android app),
 * so there is no cached task to roll back to on failure.
 */
export function useCompleteTaskById(): UpdateHookResponse<TaskId, TaskData> {
  const mutation = useTantalimMutation<ProgramTaskCompleteInput, ProgramTaskCompleteSuccess>({
    url: '/program/task/complete',
  })
  const cache = useTaskCache()

  const complete = async (taskId: TaskId): Promise<TaskData> => {
    try {
      const { task } = await mutation.mutateAsync({ taskId })
      cache.replaceItem(task)
      return task
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, complete)
}
